// @flow

import React, { PureComponent } from 'react';
import qs from 'qs';
import debounce from 'lodash.debounce';
import { Map } from 'immutable';
import { graphql, QueryRenderer } from 'react-relay';
import { i18n, Analytics } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import FilterControls, { type FilterValue } from 'main-app/components/FilterControls';
import type { RouterHistory } from 'react-router';
import getSortOptions from './getSortOptions';
import getFilterOptions from './getFilterOptions';
import PaginatedReturnsContainer from './PaginatedReturnsContainer';
import AddReturnOrderModal from 'main-app/components/AddReturnOrderModal';

type Props = { history: RouterHistory };

type State = {
  search: string,
  graphqlSearchValue: string,
  showAddModal: boolean,
};

const DEFAULT_FILTER = {
  sort: 'CREATED_AT',
  direction: 'DESC',
};

class Returns extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      search: '',
      graphqlSearchValue: '',
      showAddModal: false,
    };
    this.handleDebouncedSearch = debounce(this.handleDebouncedSearch, 500);
  }

  getFilters = (): FilterValue => {
    const { history } = this.props;
    const { search } = history.location;
    if (!search) return DEFAULT_FILTER;
    return { ...DEFAULT_FILTER, ...qs.parse(search, { ignoreQueryPrefix: true }) };
  };

  setFilters = (filters: Object) => {
    const { history } = this.props;
    history.replace(
      '/returns?' + qs.stringify(filters, { encode: true, arrayFormat: 'brackets' }),
    );
  };

  filtersDirty = () => {
    const { graphqlSearchValue } = this.state;
    return !Map(this.getFilters()).equals(Map(DEFAULT_FILTER)) || graphqlSearchValue;
  };

  compileQueryVariables = () => {
    const filters = this.getFilters();
    const { graphqlSearchValue } = this.state;
    const queryVariables = {
      ...filters,
      search: graphqlSearchValue.trim() || null,
      sortBy: { field: filters.sort, direction: filters.direction },
    };

    delete queryVariables.sort;
    delete queryVariables.direction;
    return queryVariables;
  };

  handleSearchInput = (search: string) => {
    this.setState({ search }, this.handleDebouncedSearch);
  };

  handleDebouncedSearch = () => {
    const { search } = this.state;
    this.setState({ graphqlSearchValue: search });
    Analytics.trackEvent('Search Applied', {
      listName: 'Returns',
      searchString: search,
    });
  };

  render() {
    const { history } = this.props;
    const { search, showAddModal } = this.state;

    const queryRenderer = (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query ReturnsQuery(
            $count: Int!
            $cursor: String
            $search: String
            $sortBy: ReturnOrderSort
            $statuses: [ReturnOrderStatus!]
            $returnTypes: [ReturnType!]
            $reasons: [ReturnReason!]
            $customerIds: [ID!]
            $orderIds: [ID!]
          ) {
            ...PaginatedReturnsContainer_returnOrders
              @arguments(
                search: $search
                sortBy: $sortBy
                statuses: $statuses
                returnTypes: $returnTypes
                reasons: $reasons
                customerIds: $customerIds
                orderIds: $orderIds
              )
            viewer {
              id
              permissions
              company {
                id
              }
            }
          }
        `}
        variables={{ ...this.compileQueryVariables(), count: 30, cursor: null }}
        render={({ props }) => (
          <Page>
            <PageHeaderList
              title={i18n.t('Returns')}
              searchPlaceholder={i18n.t('Customer, Order #, or RMA #')}
              searchValue={search}
              onSearchChange={this.handleSearchInput}
              createButtonLabel={i18n.t('New Return')}
              onCreateButtonClick={() => this.setState({ showAddModal: true })}
            />
            <FilterControls
              value={this.getFilters()}
              defaultValue={DEFAULT_FILTER}
              sortOptions={getSortOptions()}
              filterOptions={getFilterOptions()}
              savedFilterType="RETURN_ORDER"
              onFiltersChange={filters => this.setFilters(filters)}
            />
            {!props ? (
              <Loader />
            ) : (
              <PaginatedReturnsContainer
                history={history}
                filtersDirty={this.filtersDirty()}
                onCreateNew={() => this.setState({ showAddModal: true })}
                returnOrders={props}
              />
            )}
          </Page>
        )}
      />
    );

    return (
      <>
        {queryRenderer}
        {showAddModal && (
          <AddReturnOrderModal
            onClose={() => this.setState({ showAddModal: false })}
            onSuccess={returnOrder => {
              history.push(`/return/${returnOrder.id}`);
            }}
          />
        )}
      </>
    );
  }
}

export default Returns;
